/**
 * Formatação do Portal — moeda, data e soma.
 *
 * As três contas vêm do contrato (`centavosDe`/`deCentavos`, lote D3) e não são
 * reescritas aqui: a versão local do Portal punha o sinal só na parte inteira, e
 * −100,50 virava −99,50.
 */
import { centavosDe, deCentavos, fracaoParaPercentual, type VisaoDoPortal } from '@dv/contracts'

/**
 * O espaço fixo depois do "R$": o mesmo do `Intl.NumberFormat('pt-BR')` do app interno
 * (`apps/web/src/dinheiro.ts:30`). Impede a quebra "R$ / 4.000,00" (P09 · DS-531).
 * Escrito como escape, e não como o caractere: o byte invisível some numa edição sem ninguém ver.
 */
const ESPACO_FIXO = '\u00a0'

/**
 * O nome da VISÃO do portal (PK-06A · DS-519 · D-131). A visão não é papel de parte: "Parceiro"
 * sozinho é abreviação que o GLOSSARIO proíbe na tela; a tela diz o nome da visão.
 */
export function nomeDaVisao(visao: VisaoDoPortal): 'Portal do Parceiro' | 'Portal do Cliente' {
  return visao === 'Parceiro' ? 'Portal do Parceiro' : 'Portal do Cliente'
}

/** "1 empresa" · "3 empresas" — quantas empresas um portal reúne. */
export function quantasEmpresas(quantidade: number): string {
  return quantidade === 1 ? '1 empresa' : `${String(quantidade)} empresas`
}

export function br(valor: string): string {
  const total = centavosDe(valor)
  const negativo = total < 0n
  const absoluto = negativo ? -total : total
  const inteiro = (absoluto / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  const decimal = (absoluto % 100n).toString().padStart(2, '0')
  return `${negativo ? '−' : ''}R$${ESPACO_FIXO}${inteiro},${decimal}`
}

/**
 * O percentual do parceiro — `"0.130000"` vira `"13%"`, `"0.125"` vira `"12,5%"`.
 *
 * Esta função faltava, e a ausência tem nome: quando a tela do parceiro precisou de um
 * percentual, ele nasceu inline, cortando zeros e colando um `%` sem escalar nada. O que
 * o parceiro externo lia era **"comissão 0,13% sobre as vendas que você originou"** —
 * cem vezes menos do que ele recebe, no documento que ele usa para conferir o pagamento.
 *
 * A conta vem do contrato, como a moeda e a data: `formato.ts` é o lugar onde o Portal
 * PROCURA formatação, e a conversão em si é a mesma do resto do produto. Zero à toa não é
 * precisão, é ruído — quem corta é o conversor.
 */
export function percentualBr(fracao: string): string {
  const escalado = fracaoParaPercentual(fracao)
  return escalado === '' ? '—' : `${escalado.replace('.', ',')}%`
}

export function somar(valores: readonly string[]): string {
  return deCentavos(valores.reduce((acumulado, valor) => acumulado + centavosDe(valor), 0n))
}

/** `2026-07-20` → `20/07/2026` (GLOSSARIO §Formatação). Sem `new Date`: fuso não entra. */
export function dataBr(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
}

/** "1,2 MB" — o tamanho do documento, como o protótipo mostra. */
export function tamanhoBr(bytes: string): string {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return '—'
  if (n < 1024) return `${String(n)} B`
  if (n < 1024 * 1024) return `${(n / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} KB`
  return `${(n / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`
}

/**
 * O maior de uma lista de decimais, comparado em CENTAVOS.
 *
 * `Math.max(...valores.map(Number))` daria o mesmo resultado quase sempre e erraria
 * calado no dia em que dois valores diferissem no centavo. Dinheiro não se compara por
 * ponto flutuante nem para achar o teto de um gráfico.
 */
export function maiorDe(valores: readonly string[]): string {
  return valores.reduce((maior, valor) => (centavosDe(valor) > centavosDe(maior) ? valor : maior), '0')
}

/**
 * Quanto `valor` ocupa de `teto`, entre 0 e 1 — para GEOMETRIA, nunca para dinheiro.
 *
 * A divisão acontece sobre inteiros de centavos, com quatro casas de resolução: o que
 * sai daqui é altura de barra, não valor a pagar.
 */
export function proporcao(valor: string, teto: string): number {
  const limite = centavosDe(teto)
  if (limite <= 0n) return 0
  const parte = Number((centavosDe(valor) * 10000n) / limite) / 10000
  return parte < 0 ? 0 : parte
}

const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

/** "2026-06" vira "jun". Sem `new Date`: fuso não entra num rótulo de eixo. */
export function mesCurto(competencia: string): string {
  return MESES_CURTOS[Number(competencia.slice(5, 7)) - 1] ?? competencia
}

/**
 * "2026-09" ou "2026-09-01" (a API manda o 1º dia do mês, `0134:124`) vira "set/2026" (P07 · DS-531).
 * Sem `new Date`: fuso não entra num rótulo de mês.
 */
export function mesAno(competencia: string): string {
  return `${mesCurto(competencia)}/${competencia.slice(0, 4)}`
}

/**
 * Os `quantos` meses ("AAAA-MM") que terminam no mês de `hoje`, do mais antigo ao mais novo: a
 * janela fixa da `CurvaDeSaldo` do app interno (`tesouraria.tsx:95-99` e `:943`).
 */
export function mesesAte(hoje: string, quantos: number): string[] {
  const ultimo = Number(hoje.slice(0, 4)) * 12 + Number(hoje.slice(5, 7)) - 1
  return Array.from({ length: quantos }, (_, indice) => {
    const total = ultimo - (quantos - 1) + indice
    return `${String(Math.floor(total / 12))}-${String((total % 12) + 1).padStart(2, '0')}`
  })
}

const agrupar = (n: bigint): string => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')

/**
 * "R$ 4,0 mil" · "R$ 2,15M"; abaixo de mil, o valor inteiro (spec 11.1:81; P49 · DS-531). A escala
 * é a do app interno (`apps/web/src/dinheiro.ts:66-74`), aqui em centavos inteiros e sem ponto
 * flutuante, com o mesmo espaço fixo do `br()`.
 */
export function brCurto(valor: string): string {
  const total = centavosDe(valor)
  const negativo = total < 0n
  const absoluto = negativo ? -total : total
  if (absoluto < 100000n) return br(valor)
  const sinal = negativo ? '−' : ''
  const decimosDeMil = (absoluto + 5000n) / 10000n
  if (decimosDeMil < 10000n) return `${sinal}R$${ESPACO_FIXO}${agrupar(decimosDeMil / 10n)},${String(decimosDeMil % 10n)}${ESPACO_FIXO}mil`
  const centesimosDeMilhao = (absoluto + 500000n) / 1000000n
  return `${sinal}R$${ESPACO_FIXO}${agrupar(centesimosDeMilhao / 100n)},${(centesimosDeMilhao % 100n).toString().padStart(2, '0')}M`
}
